import type { TransactionDatePreset } from './account-transactions.model';

/** AIM path `{type}` is the inventory row `type` as returned — no remap. */
export type AccountPathType = string;

/** Trimmed inventory `type`, or null when empty (do not GET). */
export function inventoryTypeToPathType(type: string): AccountPathType | null {
  const trimmed = type.trim();
  return trimmed || null;
}

/** Any non-empty AIM type can open the shared header; known types add rich detail. */
export function canOpenAccountDetail(inventoryType: string): boolean {
  return inventoryTypeToPathType(inventoryType) !== null;
}

/** AIM transactions GET exists for each supported account detail kind. */
export function canShowAccountTransactions(inventoryType: string): boolean {
  const kind = accountDetailKind(inventoryType);
  return kind !== 'unknown';
}

/** Server-side status filter exists for repayment ledgers. */
export function canFilterTransactionStatus(inventoryType: string): boolean {
  const kind = accountDetailKind(inventoryType);
  return kind === 'debt' || kind === 'loan';
}

/** Client-side description search is Checking / Savings only. */
export function canSearchAccountTransactions(inventoryType: string): boolean {
  const kind = accountDetailKind(inventoryType);
  return kind === 'checking' || kind === 'savings';
}

/** Helix bank ledgers require a cursor; repayment ledgers advance by offset only. */
export function usesTransactionCursor(inventoryType: string): boolean {
  const kind = accountDetailKind(inventoryType);
  return kind === 'checking' || kind === 'savings';
}

export type AccountDetailKind = 'checking' | 'savings' | 'debt' | 'loan' | 'unknown';

/** Cash Advance and Loans start on all history; bank ledgers default to last 30 days. */
export function defaultTransactionDatePreset(inventoryType: string): TransactionDatePreset {
  const kind = accountDetailKind(inventoryType);
  return kind === 'debt' || kind === 'loan' ? 'all' : '30';
}

export function accountDetailKind(type: string): AccountDetailKind {
  switch (type.trim().toLowerCase()) {
    case 'checking':
    case 'spending':
      return 'checking';
    case 'savings':
      return 'savings';
    case 'debt':
    case 'earned wage access':
      return 'debt';
    case 'loan':
      return 'loan';
    default:
      return 'unknown';
  }
}
